/**
 * Button
 *
 * Shared command control for the Losi Operating Surface.
 *
 * Primary responsibilities:
 * - Provide variant and size grammar for app actions
 * - Preserve accessible names during loading states
 *
 * Dependencies:
 * - Radix Slot
 * - class-variance-authority
 * - lucide-react
 */

'use client'

import * as React from 'react'
import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'
import { Loader } from 'lucide-react'

import { cn } from '@/lib/utils'

const buttonVariants = cva(
  'ring-offset-background focus-visible:ring-primary/40 hover:bg-accent relative inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 motion-safe:transition-[background-color,border-color,color,box-shadow,transform] motion-safe:duration-200 motion-safe:ease-out active:motion-safe:translate-y-px motion-reduce:transform-none motion-reduce:transition-none',
  {
    variants: {
      variant: {
        default: 'bg-primary text-primary-foreground hover:bg-primary/90 shadow-sm hover:shadow-md',
        destructive: 'bg-destructive text-destructive-foreground hover:bg-destructive/90',
        outline:
          'border-input bg-background hover:bg-accent hover:text-accent-foreground border hover:shadow-sm',
        secondary:
          'bg-secondary text-secondary-foreground hover:bg-secondary/80 shadow-sm hover:shadow-md',
        ghost: 'hover:bg-accent hover:text-accent-foreground',
        link: 'text-primary underline-offset-4 hover:underline'
      },
      size: {
        default: 'h-10 px-4 py-2',
        xs: 'h-6 rounded-md px-2',
        sm: 'h-9 rounded-md px-3',
        lg: 'h-11 rounded-md px-8',
        icon: 'size-10'
      }
    },
    defaultVariants: {
      variant: 'default',
      size: 'default'
    }
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
  loading?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, children, asChild = false, loading = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button'
    const disabled = props.disabled

    if (asChild) {
      return (
        <Comp
          {...props}
          aria-busy={loading ? true : props['aria-busy']}
          aria-disabled={loading || disabled ? true : props['aria-disabled']}
          className={cn(buttonVariants({ variant, size, className }))}
          data-loading={loading ? true : undefined}
          ref={ref}
        >
          {children}
        </Comp>
      )
    }

    return (
      <Comp
        {...props}
        aria-busy={loading ? true : props['aria-busy']}
        className={cn(buttonVariants({ variant, size, className }))}
        data-loading={loading ? true : undefined}
        disabled={loading || disabled}
        ref={ref}
      >
        {loading ? (
          <Loader
            aria-hidden="true"
            className="absolute size-4 motion-safe:animate-spin motion-reduce:animate-none"
          />
        ) : null}
        <span
          className={cn('inline-flex items-center justify-center gap-2', loading && 'opacity-0')}
        >
          {children}
        </span>
      </Comp>
    )
  }
)
Button.displayName = 'Button'

export { Button, buttonVariants }
